/**
 * Advance Settlement & Closure Control — Stage 73.20w.
 *
 * Employee-wise advance grid + detail drawer + settlement actions +
 * special-approval modal. Backend orchestrates over existing
 * `db.loans` + `db.expense_claim_sheets` — UI is purely view + action.
 *
 * Key UX rules:
 *   - No FIFO. Each advance shows separately; admin picks per advance.
 *   - Salary-recovery is OPT-IN per advance (admin clicks button).
 *   - "+ New Advance" warns when employee already has any open advance
 *     and prompts the special-approval flow before redirecting.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Loader2, Wallet, Filter, ShieldCheck, AlertTriangle, X, FileText,
  CheckCircle2, MinusCircle, TrendingUp, Eraser, FileBarChart, Download,
  History, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_TONE = {
  open:                { bg: 'bg-sky-50',       txt: 'text-sky-700',    bd: 'border-sky-300',     label: 'Open' },
  partially_settled:   { bg: 'bg-amber-50',     txt: 'text-amber-800',  bd: 'border-amber-300',   label: 'Partially Settled' },
  fully_settled:       { bg: 'bg-emerald-50',   txt: 'text-emerald-700',bd: 'border-emerald-300', label: 'Fully Settled' },
  pending_recovery:    { bg: 'bg-violet-50',    txt: 'text-violet-700', bd: 'border-violet-300',  label: 'Pending Recovery' },
  pending_refund:      { bg: 'bg-rose-50',      txt: 'text-rose-700',   bd: 'border-rose-300',    label: 'Pending Refund' },
  written_off:         { bg: 'bg-slate-200',    txt: 'text-slate-800',  bd: 'border-slate-400',   label: 'Written Off' },
  closed:              { bg: 'bg-slate-100',    txt: 'text-slate-700',  bd: 'border-slate-300',   label: 'Closed' },
};

const ACTION_META = {
  claim_adjustment:      { label: 'Claim Adjustment', icon: CheckCircle2, tone: 'emerald' },
  salary_recovery:       { label: 'Salary Recovery',  icon: TrendingUp,   tone: 'violet' },
  employee_refund:       { label: 'Employee Refund',  icon: MinusCircle,  tone: 'amber' },
  company_reimbursement: { label: 'Company Reimburses',icon: TrendingUp,  tone: 'sky' },
  write_off:             { label: 'Write Off',        icon: Eraser,       tone: 'slate' },
  carry_forward:         { label: 'Carry Forward',    icon: ArrowRight,   tone: 'slate' },
  close:                 { label: 'Close',            icon: CheckCircle2, tone: 'emerald' },
};

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function AdvanceSettlements() {
  const [activeCompany] = useActiveCompany();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [detail, setDetail] = useState(null);
  const [showSpecialApproval, setShowSpecialApproval] = useState(false);

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!activeCompany) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({ company_id: activeCompany });
        if (filterStatus !== 'all') params.set('closure_status', filterStatus);
        const r = await axios.get(`${API}/advance-settlements/grid?${params}`, { withCredentials: true });
        if (!cancelled) setRows(r.data || []);
      } catch (e) {
        if (!cancelled) toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
      }
      if (!cancelled) setLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [activeCompany, filterStatus, reloadKey]);

  const reload = () => setReloadKey(k => k + 1);

  const summary = useMemo(() => {
    const out = { total: rows.length, advance_total: 0, pending_total: 0 };
    Object.keys(STATUS_TONE).forEach(k => (out[k] = 0));
    rows.forEach(r => {
      out.advance_total += Number(r.advance_amount || 0);
      out.pending_total += Number(r.pending_balance || 0);
      out[r.closure_status] = (out[r.closure_status] || 0) + 1;
    });
    return out;
  }, [rows]);

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="advance-settlements-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Wallet className="w-7 h-7 text-amber-600" />
              Advance Settlement & Closure
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-3xl">
              Each advance tracked separately. No FIFO — claims are mapped to a specific
              advance by explicit selection. Salary recovery is opt-in per advance.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/hr-bench/reports">
              <Button variant="outline" data-testid="advance-reports-link" className="hidden">Reports</Button>
            </Link>
            <Link to="/advance-settlements/reports">
              <Button variant="outline" data-testid="advance-reports-btn">
                <FileBarChart className="w-4 h-4 mr-1" /> Reports
              </Button>
            </Link>
            <Button
              data-testid="advance-special-approval-btn"
              onClick={() => setShowSpecialApproval(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              <ShieldCheck className="w-4 h-4 mr-1" /> Special Approval
            </Button>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SumTile label="Total Advances" value={summary.total} tone="slate" />
          <SumTile label="Total Disbursed" value={fmt(summary.advance_total)} tone="sky" big />
          <SumTile label="Total Pending" value={fmt(summary.pending_total)} tone="amber" big />
          <SumTile label="Fully Settled" value={summary.fully_settled || 0} tone="emerald" />
        </div>

        {/* Filter chips */}
        <Card className="p-4">
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <Filter className="w-4 h-4 text-slate-500" />
            {['all', ...Object.keys(STATUS_TONE)].map(s => (
              <button
                key={s}
                data-testid={`filter-status-${s}`}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1 rounded-full border font-medium capitalize ${filterStatus === s ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
                {s === 'all' ? 'All' : (STATUS_TONE[s]?.label || s.replace(/_/g, ' '))}
              </button>
            ))}
          </div>
        </Card>

        {/* Grid */}
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs">
                <tr>
                  <th className="text-left px-3 py-2">Employee</th>
                  <th className="text-left px-3 py-2">Dept / Loc</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Disbursed</th>
                  <th className="text-right px-3 py-2">Advance</th>
                  <th className="text-right px-3 py-2">Claim Sub</th>
                  <th className="text-right px-3 py-2">Settled</th>
                  <th className="text-right px-3 py-2">Recovery</th>
                  <th className="text-right px-3 py-2">Pending</th>
                  <th className="text-right px-3 py-2">Days</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-500">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading...
                  </td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-500">
                    No advances match the filter.
                  </td></tr>
                )}
                {rows.map(r => {
                  const tone = STATUS_TONE[r.closure_status] || STATUS_TONE.open;
                  return (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`advance-row-${r.id}`}>
                      <td className="px-3 py-3">
                        <div className="font-medium text-slate-800">{r.employee_name || '—'}</div>
                        <div className="text-xs text-slate-500">{r.employee_code}</div>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600">
                        <div>{r.department || '—'}</div>
                        <div className="text-slate-400">{r.location || '—'}</div>
                      </td>
                      <td className="px-3 py-3 text-xs">
                        <span className="bg-amber-50 text-amber-800 px-2 py-0.5 rounded">{r.loan_type?.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600">{r.disbursement_date || '—'}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{fmt(r.advance_amount)}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-600">{fmt(r.claim_submitted_amount)}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-emerald-700">{fmt(r.settled_amount)}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-violet-700">{fmt(r.recovery_amount)}</td>
                      <td className={`px-3 py-3 text-right tabular-nums font-semibold ${Number(r.pending_balance) > 0 ? 'text-rose-700' : Number(r.pending_balance) < 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {fmt(r.pending_balance)}
                      </td>
                      <td className="px-3 py-3 text-right text-xs text-slate-500">{r.days_pending ?? '—'}</td>
                      <td className="px-3 py-3">
                        <span className={`text-[11px] font-medium px-2 py-1 rounded border ${tone.bg} ${tone.txt} ${tone.bd}`}>{tone.label}</span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => setDetail(r)} data-testid={`advance-open-${r.id}`}>
                          <FileText className="w-3.5 h-3.5 mr-1" /> Detail
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {detail && (
        <AdvanceDetailDrawer
          advance_id={detail.id}
          onClose={() => setDetail(null)}
          onChanged={reload}
        />
      )}
      {showSpecialApproval && (
        <SpecialApprovalModal
          companyId={activeCompany}
          onClose={() => setShowSpecialApproval(false)}
          onSaved={() => { setShowSpecialApproval(false); reload(); }}
        />
      )}
    </MainLayout>
  );
}

function SumTile({ label, value, tone, big }) {
  const map = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    sky: 'bg-sky-50 border-sky-200 text-sky-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  };
  return (
    <div className={`rounded-lg border p-4 ${map[tone]}`}>
      <div className={big ? 'text-xl font-bold' : 'text-2xl font-bold'}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide opacity-80 mt-1">{label}</div>
    </div>
  );
}

function AdvanceDetailDrawer({ advance_id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [showAction, setShowAction] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const r = await axios.get(`${API}/advance-settlements/${advance_id}`, { withCredentials: true });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error('Load failed');
      }
    };
    run();
    return () => { cancelled = true; };
  }, [advance_id, reloadKey]);

  const reload = () => setReloadKey(k => k + 1);

  if (!data) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex justify-end">
        <div className="bg-white w-[700px] h-full p-6"><Loader2 className="w-6 h-6 animate-spin" /></div>
      </div>
    );
  }

  const tone = STATUS_TONE[data.closure_status] || STATUS_TONE.open;
  const canAct = data.closure_status !== 'closed' && data.closure_status !== 'written_off';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" data-testid="advance-detail-drawer">
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{data.employee_name} — {data.loan_type?.replace(/_/g, ' ')}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[11px] font-medium px-2 py-1 rounded border ${tone.bg} ${tone.txt} ${tone.bd}`}>{tone.label}</span>
              <span className="text-xs text-slate-500">{data.employee_code} • Disbursed {data.disbursement_date}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5 text-sm">
          {/* Money matrix */}
          <Card className="p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3">Settlement Matrix</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MoneyCell label="Advance" value={data.advance_amount} tone="sky" />
              <MoneyCell label="Claim Submitted" value={data.claim_submitted_amount} tone="slate" />
              <MoneyCell label="Settled" value={data.settled_amount} tone="emerald" />
              <MoneyCell label="Recovery" value={data.recovery_amount} tone="violet" />
              <MoneyCell label="Refund" value={data.refund_amount} tone="amber" />
              <MoneyCell label="Write-off" value={data.write_off_amount} tone="slate" />
              <MoneyCell label="Carry Fwd" value={data.carry_forward_amount} tone="slate" />
              <MoneyCell label="Pending Balance" value={data.pending_balance} tone={Number(data.pending_balance) > 0 ? 'rose' : Number(data.pending_balance) < 0 ? 'amber' : 'emerald'} />
            </div>
          </Card>

          {/* Claims mapped */}
          <Card className="p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3">
              Claims Mapped ({data.claims_mapped?.length || 0})
            </h3>
            {(data.claims_mapped || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">No claims have been mapped to this advance yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-slate-500 border-b">
                  <tr>
                    <th className="text-left px-2 py-1">Title</th>
                    <th className="text-left px-2 py-1">Trip</th>
                    <th className="text-left px-2 py-1">Status</th>
                    <th className="text-right px-2 py-1">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.claims_mapped.map(c => (
                    <tr key={c.id} className="border-b border-slate-100">
                      <td className="px-2 py-1">{c.title}</td>
                      <td className="px-2 py-1 text-slate-500">{c.trip_purpose || '—'}</td>
                      <td className="px-2 py-1"><span className="text-[10px] uppercase tracking-wider bg-slate-100 px-1.5 py-0.5 rounded">{c.status}</span></td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmt(c.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* Settlement actions taken */}
          <Card className="p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3 flex items-center gap-2">
              <History className="w-3.5 h-3.5" />Settlement Action History ({(data.settlement_actions || []).length})
            </h3>
            {(!data.settlement_actions || data.settlement_actions.length === 0) ? (
              <p className="text-xs text-slate-400 italic">No settlement actions yet.</p>
            ) : (
              <ul className="space-y-2">
                {data.settlement_actions.map((a, i) => {
                  const meta = ACTION_META[a.action_type] || { label: a.action_type, icon: FileText, tone: 'slate' };
                  const Icon = meta.icon;
                  return (
                    <li key={i} className="border-l-4 border-violet-300 pl-3 py-1 bg-violet-50/40 rounded">
                      <div className="text-xs font-medium text-slate-800 flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5" />
                        {meta.label} — {fmt(a.amount)}
                        {a.payroll_month && <span className="text-[10px] bg-violet-100 px-1.5 rounded">{a.payroll_month}</span>}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {a.by_name || '—'} • {a.effective_date || '—'} {a.approved_by && `• approved by ${a.approved_by}`}
                      </div>
                      {a.remarks && <div className="text-xs italic text-slate-600 mt-0.5">&ldquo;{a.remarks}&rdquo;</div>}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex flex-wrap justify-end gap-2">
          {canAct && Object.entries(ACTION_META).map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <Button
                key={key}
                size="sm"
                variant="outline"
                onClick={() => setShowAction(key)}
                data-testid={`advance-action-${key}`}
              >
                <Icon className="w-3.5 h-3.5 mr-1" /> {meta.label}
              </Button>
            );
          })}
        </div>
      </div>

      {showAction && (
        <ActionModal
          advance_id={advance_id}
          actionType={showAction}
          pendingBalance={data.pending_balance}
          onClose={() => setShowAction(null)}
          onSaved={() => { setShowAction(null); reload(); onChanged(); }}
        />
      )}
    </div>
  );
}

function MoneyCell({ label, value, tone }) {
  const map = {
    sky: 'bg-sky-50 text-sky-800 border-sky-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <div className={`rounded p-2 border ${map[tone] || map.slate} text-center`}>
      <div className="text-sm font-semibold tabular-nums">{fmt(value)}</div>
      <div className="text-[10px] uppercase tracking-wide opacity-80 mt-0.5">{label}</div>
    </div>
  );
}

function ActionModal({ advance_id, actionType, pendingBalance, onClose, onSaved }) {
  const meta = ACTION_META[actionType] || {};
  const [amount, setAmount] = useState(actionType === 'close' ? '' : String(pendingBalance || 0));
  const [remarks, setRemarks] = useState('');
  const [payrollMonth, setPayrollMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [approvedBy, setApprovedBy] = useState('');
  const [busy, setBusy] = useState(false);

  const needsPayrollMonth = actionType === 'salary_recovery';
  const needsApproval = actionType === 'write_off';
  const needsAmount = actionType !== 'close';

  const save = async () => {
    if (needsAmount && (!amount || Number(amount) <= 0)) { toast.error('Amount > 0 required'); return; }
    if (needsPayrollMonth && !payrollMonth) { toast.error('Payroll month required for salary recovery'); return; }
    if (needsApproval && !approvedBy.trim()) { toast.error('Approver name required for write-off'); return; }
    setBusy(true);
    try {
      const body = {
        action_type: actionType,
        amount: Number(amount || 0),
        remarks: remarks.trim(),
        payroll_month: needsPayrollMonth ? payrollMonth : '',
        approved_by: needsApproval ? approvedBy.trim() : '',
      };
      const r = await axios.post(`${API}/advance-settlements/${advance_id}/action`, body, { withCredentials: true });
      if (r.data?.monthly_input_emitted) {
        toast.success(`Action saved. Salary deduction will appear on payslip for ${payrollMonth}.`);
      } else {
        toast.success('Action saved');
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-6 bg-white" data-testid="advance-action-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">{meta.label}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs">
            Pending balance: <strong>{fmt(pendingBalance)}</strong>
            {actionType === 'salary_recovery' && (
              <div className="text-violet-700 mt-1">
                ⚙ This action will automatically add a deduction line in the selected month&apos;s payroll run.
              </div>
            )}
          </div>
          {needsAmount && (
            <div>
              <label className="block text-xs font-medium mb-1">Amount *</label>
              <input data-testid="action-amount" type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
            </div>
          )}
          {needsPayrollMonth && (
            <div>
              <label className="block text-xs font-medium mb-1">Payroll Month *</label>
              <input data-testid="action-payroll-month" type="month" value={payrollMonth} onChange={e => setPayrollMonth(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
              <p className="text-[10px] text-slate-500 mt-1">Format: YYYY-MM. Engine reads this when processing that month&apos;s payroll.</p>
            </div>
          )}
          {needsApproval && (
            <div>
              <label className="block text-xs font-medium mb-1">Approved By *</label>
              <input data-testid="action-approved-by" value={approvedBy} onChange={e => setApprovedBy(e.target.value)} placeholder="e.g. CFO John Smith" className="w-full px-3 py-2 border rounded text-sm" />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium mb-1">Remarks</label>
            <textarea data-testid="action-remarks" rows={2} value={remarks} onChange={e => setRemarks(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="action-save" onClick={save} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Action'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function SpecialApprovalModal({ companyId, onClose, onSaved }) {
  const [employeeId, setEmployeeId] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [requestedAmount, setRequestedAmount] = useState('');
  const [reason, setReason] = useState('');
  const [approvedBy, setApprovedBy] = useState('');
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!employeeId) { setSnapshot(null); return undefined; }
    const ctrl = new AbortController();
    axios.get(`${API}/advance-settlements/employee/${employeeId}/open-advances`,
      { withCredentials: true, signal: ctrl.signal })
      .then(r => setSnapshot(r.data))
      .catch(e => { if (e.name !== 'CanceledError') setSnapshot(null); });
    return () => ctrl.abort();
  }, [employeeId]);

  const save = async () => {
    if (!employeeId || !requestedAmount || !reason || !approvedBy) {
      toast.error('Employee, amount, reason and approver are required'); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/advance-settlements/special-approval`, {
        employee_id: employeeId, company_id: companyId,
        requested_amount: Number(requestedAmount),
        reason, approved_by: approvedBy, remarks,
      }, { withCredentials: true });
      toast.success('Special approval recorded. You may now release the new advance.');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="special-approval-modal">
      <Card className="max-w-lg w-full p-6 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Special Approval — Additional Advance</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-slate-600 mb-4">
          Use this when an employee already has an open or partially-settled advance
          and a new advance is genuinely needed. The current pending picture is
          captured automatically for the audit trail.
        </p>
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs font-medium mb-1">Employee ID *</label>
            <input data-testid="sa-employee-id" value={employeeId} onChange={e => setEmployeeId(e.target.value)} placeholder="Employee internal id" className="w-full px-3 py-2 border rounded text-sm font-mono" />
          </div>
          {snapshot && (
            <Card className={`p-3 ${snapshot.requires_special_approval ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'}`}>
              {snapshot.requires_special_approval ? (
                <>
                  <div className="flex items-start gap-2 text-xs">
                    <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-amber-900">
                      <strong>{snapshot.open_count}</strong> unsettled advance{snapshot.open_count === 1 ? '' : 's'} found. New advance requires special approval.
                    </div>
                  </div>
                  <ul className="mt-2 text-[11px] text-amber-800 list-disc list-inside">
                    {snapshot.open_advances.map(a => (
                      <li key={a.id}>{fmt(a.advance_amount)} disbursed {a.disbursement_date} • pending {fmt(a.pending_balance)} • <span className="capitalize">{a.closure_status?.replace(/_/g, ' ')}</span></li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="text-xs text-emerald-800"><CheckCircle2 className="w-4 h-4 inline mr-1" /> No open advances. Special approval not strictly required.</div>
              )}
            </Card>
          )}
          <div>
            <label className="block text-xs font-medium mb-1">Requested Amount *</label>
            <input data-testid="sa-amount" type="number" min="0" value={requestedAmount} onChange={e => setRequestedAmount(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Reason *</label>
            <textarea data-testid="sa-reason" rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="Why is this advance needed despite pending settlement?" className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Approved By *</label>
            <input data-testid="sa-approved-by" value={approvedBy} onChange={e => setApprovedBy(e.target.value)} placeholder="e.g. CFO Jane Doe" className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Approver Remarks</label>
            <textarea data-testid="sa-remarks" rows={2} value={remarks} onChange={e => setRemarks(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="sa-save" onClick={save} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Record Special Approval'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
